import { useTheme } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { TFunction } from 'i18next';
import { INDICATOR_TAG_TYPES } from 'modules/decision/constants';
import type { IndicatorTagT } from 'modules/decision/types/indicators';
import {
    useEffect,
    useLayoutEffect,
    useReducer,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';

const FIELD_TYPES = [
    INDICATOR_TAG_TYPES.metric,
    INDICATOR_TAG_TYPES.dimension,
    INDICATOR_TAG_TYPES.filter,
] as const;

const MAX_METRIC_LINES = 3;

// Renders values and shows "+X" for items hidden by overflow.
// `maxLines` controls single-line (horizontal) vs multi-line (vertical) overflow.
const OverflowValues = ({
    values,
    maxLines = 1,
}: {
    values: string[];
    maxLines?: number;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(values.length);
    const [revision, bumpRevision] = useReducer((r: number) => r + 1, 0);
    const multiLine = maxLines > 1;

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el || visibleCount <= 1) return;
        let overflows: boolean;
        if (multiLine) {
            const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
            overflows = el.scrollHeight > lineHeight * (maxLines + 1);
        } else {
            overflows = el.scrollWidth > el.clientWidth;
        }
        if (overflows) {
            setVisibleCount((c) => c - 1);
        }
    }, [visibleCount, revision, multiLine, maxLines]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const observer = new ResizeObserver(() => {
            setVisibleCount(values.length);
            bumpRevision();
        });
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const hiddenCount = values.length - visibleCount;
    const text = values.slice(0, visibleCount).join(', ');
    const suffix = hiddenCount > 0 ? `, +${hiddenCount}` : '';

    return (
        <Tooltip title={values.join(', ')}>
            <Flex
                ref={containerRef}
                align={multiLine ? undefined : 'center'}
                flex={1}
                minWidth={0}
                overflow="hidden"
                sx={
                    multiLine
                        ? {
                              maxHeight: `calc(${maxLines} * 1.5em)`,
                          }
                        : undefined
                }
            >
                {multiLine ? (
                    <Typography variant="body2SemiBold">
                        {text}
                        {suffix}
                    </Typography>
                ) : (
                    <>
                        <Typography
                            variant="body2SemiBold"
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {text}
                        </Typography>
                        {hiddenCount > 0 && (
                            <Typography
                                variant="body2SemiBold"
                                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                            >
                                {suffix}
                            </Typography>
                        )}
                    </>
                )}
            </Flex>
        </Tooltip>
    );
};

const getFieldValueContent = (
    type: (typeof FIELD_TYPES)[number],
    values: string[],
    t: TFunction,
): JSX.Element => {
    if (values.length === 0) {
        return (
            <Typography variant="body2" color="text.disabled">
                {t(`indicators.fields.NoValues.${type}`)}
            </Typography>
        );
    }
    if (type === INDICATOR_TAG_TYPES.metric) {
        return (
            <OverflowValues
                key={values.join(',')}
                values={values}
                maxLines={MAX_METRIC_LINES}
            />
        );
    }
    return <OverflowValues key={values.join(',')} values={values} />;
};

type Props = {
    indicatorTags: IndicatorTagT[];
};

const IndicatorFields = ({ indicatorTags }: Props): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Flex direction="column" gap={1}>
            {FIELD_TYPES.map((type) => {
                const values = indicatorTags
                    .filter((tag) => tag.type === type)
                    .map((tag) => tag.value);

                return (
                    <Flex key={type} align="center" gap={1}>
                        <Typography
                            sx={{
                                color: theme.palette.mediumGrey,
                                flexShrink: 0,
                            }}
                        >
                            {t(`indicators.fields.sectionTitles.${type}`)}
                        </Typography>
                        {getFieldValueContent(type, values, t)}
                    </Flex>
                );
            })}
        </Flex>
    );
};

export default IndicatorFields;
